/**
 * uklad-ekranu.js — JEDNO miejsce, które mówi Studiu, na jakim ekranie i czym jest obsługiwane
 * (plan `SAAS/PLAN_studio_telefon_tablet.md` §3, decyzja operatora 2026-09-30 „tak”).
 *
 * Dwie NIEZALEŻNE osie, nie „pas tabletu”:
 *   - szerokość steruje UKŁADEM: < 768 px telefon · 768–1279 px wąski · ≥ 1280 px dzisiejszy układ;
 *   - rodzaj wskaźnika steruje tylko ROZMIAREM celów (palec → ≥ 44 px, kosz przy koncie zawsze widoczny).
 * Laptop z ekranem dotykowym zgłasza mysz jako główny wskaźnik — dostaje komputer bez zmian.
 *
 * 🔴 Odczyt w inicjalizatorze stanu jest SYNCHRONICZNY celowo: pierwsza klatka komputera (≥ 1280 px,
 * mysz) musi od razu być dzisiejszym układem, inaczej zrzut przed/po różni się przez przypadek.
 *
 * ⚠️ Zachowanie zaznaczania NIE czyta tego haka — o dotyku rozstrzyga KAŻDE zdarzenie osobno
 * (`pointerType`), bo iPad z klawiaturą ma i palec, i Shift (panel na planie).
 */
import React from 'react';

export const ZAPYTANIE_TELEFON = '(max-width: 767.98px)';
export const ZAPYTANIE_WASKI = '(max-width: 1279.98px)';
export const ZAPYTANIE_DOTYK = '(pointer: coarse), (hover: none)';

function czytaj() {
  const mm = typeof window !== 'undefined' && window.matchMedia;
  if (!mm) return { telefon: false, waski: false, dotyk: false };
  const telefon = mm(ZAPYTANIE_TELEFON).matches;
  return {
    telefon,
    // `waski` obejmuje też telefon: oba chowają listę kont do szuflady i kładą pasek na dole.
    waski: telefon || mm(ZAPYTANIE_WASKI).matches,
    dotyk: mm(ZAPYTANIE_DOTYK).matches,
  };
}

/** { telefon, waski, dotyk } — przelicza się sam przy obrocie urządzenia i zmianie okna. */
export function useUkladEkranu() {
  const [uklad, setUklad] = React.useState(czytaj);
  React.useEffect(() => {
    const mm = window.matchMedia;
    if (!mm) return undefined;
    const zapytania = [ZAPYTANIE_TELEFON, ZAPYTANIE_WASKI, ZAPYTANIE_DOTYK].map((z) => mm(z));
    const naZmiane = () => setUklad((stary) => {
      const nowy = czytaj();
      return (stary.telefon === nowy.telefon && stary.waski === nowy.waski && stary.dotyk === nowy.dotyk) ? stary : nowy;
    });
    zapytania.forEach((z) => (z.addEventListener ? z.addEventListener('change', naZmiane) : z.addListener(naZmiane)));
    naZmiane();   // zmiana między odczytem a podłączeniem nasłuchu nie może przepaść
    return () => zapytania.forEach((z) => (z.removeEventListener ? z.removeEventListener('change', naZmiane) : z.removeListener(naZmiane)));
  }, []);
  return uklad;
}

/**
 * Okno wysuwane (szuflada kont z boku, „Więcej” z dołu) — dostępność z planu §4 pkt 1g:
 * Escape i dotknięcie tła zamykają · fokus wchodzi do okna i wraca na przycisk, który je otworzył ·
 * `role="dialog"` + `aria-modal` · Tab nie wychodzi poza okno.
 * Blokadę przewijania strony pod spodem robi wołający (siatka Studia to osobny przewijany obszar).
 */
export function OknoWysuwane({ strona, etykieta, onZamknij, powrot, children }) {
  const oknoRef = React.useRef(null);
  const zamknijRef = React.useRef(onZamknij);
  zamknijRef.current = onZamknij;
  React.useEffect(() => {
    const okno = oknoRef.current;
    // Fokus na PIERWSZY AKTYWNY cel — wyłączony przycisk fokusu nie przyjmie i ten zostałby na <body>.
    const pierwszy = okno && okno.querySelector('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    (pierwszy || okno) && (pierwszy || okno).focus();
    // Escape zamyka okno także wtedy, gdy fokus mimo wszystko wypadł z okna (np. na <body>).
    // stopPropagation (P4, 2026-10-02 — zmierzone): React przerysowuje stronę w mikrozadaniu MIĘDZY tym
    // nasłuchem (document) a nasłuchem siatki Studia (window), więc ten sam Escape, który zamknął okno,
    // docierał do siatki jako „okno już zamknięte” i ODZNACZAŁ wszystkie zdjęcia — wbrew R12.5
    // („otwarte okno przejmuje klawisze”). Usterka stała od R12 (ta sama na paczce sprzed P4).
    const naEscape = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); zamknijRef.current(); } };
    document.addEventListener('keydown', naEscape);
    const przycisk = powrot && powrot.current;
    return () => {
      document.removeEventListener('keydown', naEscape);
      // Powrót fokusu tylko na przycisk, który go przyjmie (np. „Więcej” bywa wyłączony po „Odznacz wszystko”).
      if (przycisk && document.contains(przycisk) && !przycisk.disabled) przycisk.focus();
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const naKlawisz = (e) => {
    if (e.key === 'Escape') return;   // obsługuje nasłuch dokumentu wyżej — jedno zamknięcie na jedno naciśnięcie
    if (e.key !== 'Tab') return;
    const cele = [...oknoRef.current.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])')];
    if (cele.length === 0) { e.preventDefault(); return; }
    const pierwszy = cele[0], ostatni = cele[cele.length - 1];
    if (e.shiftKey && document.activeElement === pierwszy) { e.preventDefault(); ostatni.focus(); }
    else if (!e.shiftKey && document.activeElement === ostatni) { e.preventDefault(); pierwszy.focus(); }
  };
  const zBoku = strona === 'lewo';
  return (
    <div onKeyDown={naKlawisz} style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div onClick={onZamknij} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} />
      <div ref={oknoRef} role="dialog" aria-modal="true" aria-label={etykieta} tabIndex={-1}
        style={zBoku ? {
          position: 'absolute', top: 0, bottom: 0, left: 0, width: 'min(320px, 86vw)',
          display: 'flex', flexDirection: 'column', outline: 'none',
          boxShadow: 'var(--shadow)',   // P4: cień z tokenów (wyglad.css) — w jasnym motywie bez czarnej poświaty
        } : {
          position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '80%', overflowY: 'auto',
          background: 'var(--panel)', borderTop: '1px solid var(--line-2)', borderRadius: '14px 14px 0 0',
          padding: '8px 0 calc(8px + env(safe-area-inset-bottom))', outline: 'none',
          boxShadow: 'var(--shadow)',
        }}>
        {children}
      </div>
    </div>
  );
}
